import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { WizardStep } from '../../domain';
import { getExperienceType } from '../../selectors';
import { eventcreationWizardPath, TEMPLATES } from './constants';

export interface UseWizardNavigationReturn {
  currentStepIndex: number;
  goToStep: (stepIndex: number) => void;
  goToNextStep: () => void;
  initializeSteps: () => void;
  steps: WizardStep[];
  goBack: () => void;
  redirectToDashboard: () => void;
  isLastStep: boolean;
}

export const useWizardNavigation = (): UseWizardNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationSlug } = useParams();
  const experienceType = useSelector(getExperienceType);
  const steps = TEMPLATES[experienceType].steps;

  const currentStepIndex = useMemo(() => {
    return steps.findIndex(step => location.pathname.includes(step.path));
  }, [steps, location.pathname]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex < 0 || stepIndex >= steps.length) {
        return;
      }

      if (stepIndex === currentStepIndex) {
        return;
      }

      const targetStep = steps[stepIndex];
      navigate(`..${targetStep.path}`);
    },
    [steps, currentStepIndex, navigate],
  );

  const goBack = useCallback(() => {
    const previousStep = steps[currentStepIndex - 1];

    if (previousStep) {
      navigate(`..${previousStep.path}`);
      return;
    }

    if (!organizationSlug) {
      navigate(`${eventcreationWizardPath}/experience-details`);
      return;
    }

    navigate(
      `/org/${organizationSlug}${eventcreationWizardPath}/experience-details`,
    );
  }, [steps, currentStepIndex, navigate, organizationSlug]);

  const goToNextStep = useCallback(() => {
    const nextStepIndex = currentStepIndex + 1;

    if (nextStepIndex < steps.length) {
      goToStep(nextStepIndex);
    }
  }, [currentStepIndex, goToStep, steps.length]);

  const initializeSteps = useCallback(() => {
    const steps = TEMPLATES[experienceType].steps;
    navigate(`..${steps[0].path}`);
  }, [experienceType, navigate]);

  const redirectToDashboard = useCallback(() => {
    if (!organizationSlug) {
      navigate('/attendee', { replace: true });
      return;
    }

    navigate(`/org/${organizationSlug}`, { replace: true });
  }, [navigate, organizationSlug]);

  const isLastStep = useMemo(() => {
    return currentStepIndex === steps.length - 1;
  }, [currentStepIndex, steps]);

  return {
    initializeSteps,
    currentStepIndex,
    steps,
    goToStep,
    goToNextStep,
    goBack,
    redirectToDashboard,
    isLastStep,
  };
};
